import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {FiChevronDown, FiCheck, FiMapPin} from "react-icons/fi";
import {USER_SET_ACTIVE_AREAL} from "../../../reducers/user";
import Modal from "../../../components/My/Modal";

// jemná animace rozbalení menu + pulzující šipka pro scroll
const DROPDOWN_KEYFRAMES = `@keyframes areaSelectorPop{from{opacity:0;transform:translateY(-6px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}@keyframes areaSelectorBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(3px)}}`;

// ─── custom dropdown ────────────────────────────────────────────────────────────

const Dropdown = ({options, value, onPick, compact}) => {
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(null);
    const [triggerHot, setTriggerHot] = useState(false);
    // canUp/canDown = je obsah skrytý nad/pod viditelnou oblastí (lze scrollovat)
    const [scroll, setScroll] = useState({canUp: false, canDown: false});
    const ref = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const updateScroll = () => {
        const el = listRef.current;
        if (!el) return;
        const canUp = el.scrollTop > 2;
        const canDown = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
        setScroll(prev => (prev.canUp === canUp && prev.canDown === canDown) ? prev : {canUp, canDown});
    };

    // po otevření změř, zda je co scrollovat
    useEffect(() => {
        if (open) requestAnimationFrame(updateScroll);
        else setScroll({canUp: false, canDown: false});
    }, [open]);

    const current = options.find(o => String(o.id) === String(value));

    const borderColor = open ? "#2563eb" : triggerHot ? "#94a3b8" : "#cbd5e1";
    const triggerStyle = {
        display: "flex", alignItems: "center", gap: "8px",
        padding: compact ? "6px 10px" : "8px 12px",
        border: `1px solid ${borderColor}`,
        borderRadius: "9px",
        background: open ? "#fff" : triggerHot ? "#f8fafc" : "#fff",
        color: "#1e293b", cursor: "pointer",
        fontSize: compact ? "var(--enigoo-fs-sm)" : "var(--enigoo-fs-md)", fontWeight: 600,
        outline: "none", width: compact ? "auto" : "100%",
        minWidth: compact ? "160px" : undefined, maxWidth: compact ? "210px" : undefined,
        boxShadow: open ? "0 0 0 3px rgba(37,99,235,0.12)" : "none",
        transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
    };

    return (
        <div ref={ref} style={{position: "relative", width: compact ? "auto" : "100%"}}>
            <style>{DROPDOWN_KEYFRAMES}</style>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                onMouseEnter={() => setTriggerHot(true)}
                onMouseLeave={() => setTriggerHot(false)}
                style={triggerStyle}
            >
                <span style={{
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    width: "22px", height: "22px", borderRadius: "6px", background: "#eff6ff", color: "#2563eb",
                }}>
                    <FiMapPin size={12}/>
                </span>
                <span style={{flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left"}}>{current?.name ?? "—"}</span>
                <FiChevronDown size={14} style={{flexShrink: 0, color: open ? "#2563eb" : "#94a3b8", transform: open ? "rotate(180deg)" : "none", transition: "transform 0.18s, color 0.15s"}}/>
            </button>

            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 6px)", right: 0, left: "auto",
                    minWidth: compact ? "200px" : "100%", background: "#fff",
                    border: "1px solid #e8edf3", borderRadius: "11px",
                    boxShadow: "0 12px 32px rgba(15,23,42,0.16), 0 2px 6px rgba(15,23,42,0.06)",
                    padding: "5px", zIndex: 60,
                    transformOrigin: "top right", animation: "areaSelectorPop 0.14s ease-out",
                }}>
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px",
                        padding: "5px 9px 7px", fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700,
                        color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em",
                        borderBottom: "1px solid #f1f5f9", marginBottom: "4px",
                    }}>
                        <span>Přepnout areál</span>
                        <span style={{
                            background: "#eff6ff", color: "#2563eb", borderRadius: "999px",
                            padding: "1px 7px", fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700, letterSpacing: 0,
                        }}>
                            {options.length}
                        </span>
                    </div>

                    <div style={{position: "relative"}}>
                        <div
                            ref={listRef}
                            onScroll={updateScroll}
                            style={{maxHeight: "240px", overflowY: "auto"}}
                        >
                            {options.map(o => {
                                const active = String(o.id) === String(value);
                                const hot = hovered === o.id;
                                return (
                                    <button
                                        key={o.id} type="button"
                                        onMouseEnter={() => setHovered(o.id)}
                                        onMouseLeave={() => setHovered(null)}
                                        onClick={() => { setOpen(false); onPick(o); }}
                                        style={{
                                            display: "flex", alignItems: "center", gap: "9px",
                                            width: "100%", textAlign: "left", padding: "8px 9px", border: "none",
                                            borderRadius: "7px", cursor: "pointer",
                                            background: active ? "#eff6ff" : hot ? "#f5f7fa" : "transparent",
                                            color: active ? "#2563eb" : "#334155",
                                            fontSize: "var(--enigoo-fs-sm)", fontWeight: active ? 700 : 500,
                                            transition: "background 0.12s",
                                        }}
                                    >
                                        <span style={{
                                            width: "7px", height: "7px", borderRadius: "50%", flexShrink: 0,
                                            background: active ? "#2563eb" : "#cbd5e1",
                                        }}/>
                                        <span style={{flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{o.name}</span>
                                        {active && <FiCheck size={15} style={{flexShrink: 0}}/>}
                                    </button>
                                );
                            })}
                        </div>

                        {/* fade nahoře — skrytý obsah výše */}
                        {scroll.canUp && (
                            <div style={{
                                position: "absolute", top: 0, left: 0, right: 0, height: "22px",
                                background: "linear-gradient(to bottom, #fff 30%, rgba(255,255,255,0))",
                                pointerEvents: "none", borderRadius: "7px 7px 0 0",
                            }}/>
                        )}

                        {/* fade dole + pulzující šipka — další areály ke scrollu */}
                        {scroll.canDown && (
                            <div style={{
                                position: "absolute", bottom: 0, left: 0, right: 0, height: "30px",
                                background: "linear-gradient(to top, #fff 35%, rgba(255,255,255,0))",
                                pointerEvents: "none", borderRadius: "0 0 7px 7px",
                                display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: "3px",
                            }}>
                                <FiChevronDown size={15} color="#2563eb" style={{animation: "areaSelectorBounce 1.1s ease-in-out infinite"}}/>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── area selector ──────────────────────────────────────────────────────────────

const AreaSelector = ({onArealChange, compact = false} = {}) => {
    const user = useSelector(s => s.user);
    const dispatch = useDispatch();
    // areál čekající na potvrzení v modalu (null = modal zavřený)
    const [pending, setPending] = useState(null);

    const arealList = user.user?.areal ?? [];
    const activeAreal = user.activeAreal;

    // pokud není žádný aktivní areál, vyber první (jednorázově)
    useEffect(() => {
        if (!activeAreal && arealList.length > 0) {
            dispatch({type: USER_SET_ACTIVE_AREAL, payload: arealList[0]});
        }
    }, []);

    if (!arealList.length || arealList.length === 1) return null;

    // výběr v dropdownu jen otevře potvrzovací modal, změna proběhne až po potvrzení
    const onSelect = (obj) => {
        if (!obj || (activeAreal && String(obj.id) === String(activeAreal.id))) return;
        setPending(obj);
    };

    const confirm = () => {
        dispatch({type: USER_SET_ACTIVE_AREAL, payload: pending});
        if (onArealChange) onArealChange(pending);
        setPending(null);
    };

    const cancel = () => setPending(null);

    const confirmModal = (
        <Modal
            visible={!!pending}
            close={cancel}
            title="Změna areálu"
            body={
                <div style={{fontSize: "var(--enigoo-fs-md)", color: "#475569", lineHeight: 1.5}}>
                    Opravdu chcete změnit areál z{" "}
                    <strong style={{color: "#1e293b"}}>{activeAreal?.name ?? "—"}</strong> na{" "}
                    <strong style={{color: "#1e293b"}}>{pending?.name ?? "—"}</strong>?
                </div>
            }
            footer={
                <>
                    <button
                        onClick={cancel}
                        style={{padding: "7px 14px", background: "#fff", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "7px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600, cursor: "pointer"}}
                    >
                        Zrušit
                    </button>
                    <button
                        onClick={confirm}
                        style={{padding: "7px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "7px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 700, cursor: "pointer"}}
                    >
                        Potvrdit
                    </button>
                </>
            }
        />
    );

    if (compact) {
        return (
            <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                <span style={{fontSize: "var(--enigoo-fs-xs)", fontWeight: 600, color: "#64748b", whiteSpace: "nowrap"}}>Areál:</span>
                <Dropdown options={arealList} value={activeAreal?.id ?? ""} onPick={onSelect} compact/>
                {confirmModal}
            </div>
        );
    }

    return (
        <div style={{background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "10px 14px", marginBottom: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)"}}>
            <label style={{display: "block", fontSize: "var(--enigoo-fs-xs)", fontWeight: 600, color: "#64748b", marginBottom: "5px"}}>
                Areál: <span style={{color: "#ef4444"}}>*</span>
            </label>
            <Dropdown options={arealList} value={activeAreal?.id ?? ""} onPick={onSelect} compact={false}/>
            {confirmModal}
        </div>
    );
};

export default AreaSelector;
